/**
 * CORE-M-04 Campaign Review copy. Plain words, no em dashes, no internal
 * references. Kept apart from the component so each line is testable.
 */
import type {
  CampaignReviewConversionRule,
  CampaignReviewNotChecked,
  CampaignReviewRow,
  CampaignReviewSnoozeReason,
  CampaignReviewTargetSource,
  CampaignReviewWindow,
} from '@/services/googleAdsCampaignReviewService';

export function usd(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'n/a';
  return value >= 100
    ? `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function conversions(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export const TAB_INTRO =
  'Campaigns that have had enough time and spend and still did badly over both the last 30 and the last 90 days. Brand campaigns are never flagged. The action is to raise it with the client, because some clients want a service advertised whatever it returns.';

export const PAUSE_HINT = 'To pause a campaign, use Campaign structure below.';

export function checkedLine(count: number): string {
  return `Checked ${count} ${plural(count, 'campaign', 'campaigns')}.`;
}

export function noneNeedActionLine(count: number): string {
  return `Checked ${count} ${plural(count, 'campaign', 'campaigns')} for at least 30 days of delivery, no big bid or budget change in the last 14 days, spend of at least 3 times the target CPA with 60 or more clicks, and no qualified conversions or a CPA of at least twice the target over both 30 and 90 days. None need action.`;
}

/** One line per reason a campaign was not checked; zero counts are left out. */
export function notCheckedLines(nc: CampaignReviewNotChecked): string[] {
  const lines: string[] = [];
  const n = (k: keyof CampaignReviewNotChecked) => nc[k] ?? 0;
  if (n('no_target') > 0) {
    lines.push(`${n('no_target')} ${plural(n('no_target'), 'campaign', 'campaigns')} not checked: no target CPA set`);
  }
  if (n('brand') > 0) {
    lines.push(`${n('brand')} brand ${plural(n('brand'), 'campaign', 'campaigns')} not checked`);
  }
  if (n('not_enough_history') > 0) {
    lines.push(`${n('not_enough_history')} ${plural(n('not_enough_history'), 'campaign', 'campaigns')} not checked yet: fewer than 30 days of delivery`);
  }
  if (n('recent_change') > 0) {
    lines.push(`${n('recent_change')} ${plural(n('recent_change'), 'campaign', 'campaigns')} not checked: a bid strategy change or a budget move over 20% in the last 14 days`);
  }
  if (n('change_history_unavailable') > 0) {
    lines.push(`${n('change_history_unavailable')} ${plural(n('change_history_unavailable'), 'campaign', 'campaigns')} not checked: Core could not read the last 14 days of changes`);
  }
  return lines;
}

export function trackingQuietLine(reason: string): string {
  return `Nothing is flagged while tracking has a problem. ${reason} Once tracking is fixed, this tab checks campaigns again.`;
}

export function ruleLabel(rule: CampaignReviewConversionRule | null): string {
  return rule === 'whatconverts'
    ? "Qualified conversions: WhatConverts's own conversion actions."
    : 'Qualified conversions: what Google counts for bidding.';
}

/** Google's bid strategy types, in the words Google Ads shows. */
const STRATEGY_WORDS: Record<string, string> = {
  MAXIMIZE_CONVERSIONS: 'Maximize conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Maximize conversion value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_SPEND: 'Maximize clicks',
  MAXIMIZE_CLICKS: 'Maximize clicks',
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  TARGET_IMPRESSION_SHARE: 'Target impression share',
};

export function strategyWords(type: string | null | undefined): string {
  if (!type) return 'a bid strategy with no target CPA';
  const known = STRATEGY_WORDS[type.toUpperCase()];
  if (known) return known;
  const words = type.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Where the target came from. A client-target fallback says what the
 *  campaign itself uses, so it is never mistaken for the campaign's own. */
export function targetLabel(
  target: number | null,
  source: CampaignReviewTargetSource | null,
  campaignStrategy?: string | null,
  portfolioUnreadable?: boolean,
): string {
  if (source === 'campaign') {
    return `Target CPA ${usd(target)}, from the campaign's Google Ads bid strategy.`;
  }
  if (source === 'portfolio') {
    return `Target CPA ${usd(target)}, from the shared bid strategy this campaign uses in Google Ads.`;
  }
  const uses = portfolioUnreadable
    ? `a shared ${strategyWords(campaignStrategy)} strategy that Core could not read`
    : strategyWords(campaignStrategy);
  return `Target CPA ${usd(target)}, from the client's setting in Core (the campaign uses ${uses}).`;
}

/** "qualified leads" only when WhatConverts's own actions are the source. */
export function conversionsNoun(rule: CampaignReviewConversionRule | null): string {
  return rule === 'whatconverts' ? 'qualified leads' : 'conversions Google uses for bidding';
}

export function conversionsColumn(rule: CampaignReviewConversionRule | null): string {
  return rule === 'whatconverts' ? 'Qualified leads' : 'Bidding conv.';
}

export const SNOOZE_REASON_LABEL: Record<CampaignReviewSnoozeReason, string> = {
  wrong: 'Wrong flag',
  not_now: 'Not now',
  need_to_check: 'Need to check',
};

function windowSentence(w: CampaignReviewWindow, leads: boolean): string {
  const clicks = `${usd(w.spend_usd)} on ${w.clicks.toLocaleString('en-US')} clicks`;
  // Client-facing: "qualified leads" only when WhatConverts qualified them;
  // otherwise these are the conversions Google Ads counts, which are not
  // all leads.
  if (w.qualified_conversions <= 0) {
    return leads
      ? `Over the last ${w.days} days it spent ${clicks} and brought no qualified leads.`
      : `Over the last ${w.days} days it spent ${clicks} and Google Ads counted no conversions.`;
  }
  const n = conversions(w.qualified_conversions);
  return leads
    ? `Over the last ${w.days} days it spent ${clicks} for ${n} qualified ${plural(w.qualified_conversions, 'lead', 'leads')}, about ${usd(w.cpa_usd)} per lead.`
    : `Over the last ${w.days} days it spent ${clicks} for ${n} ${plural(w.qualified_conversions, 'conversion', 'conversions')} counted by Google Ads, about ${usd(w.cpa_usd)} each.`;
}

/** What gets copied for the client: plain, no internal terms. */
export function clientSummary(row: CampaignReviewRow): string {
  const leads = row.conversion_rule === 'whatconverts';
  const lines = [
    `${row.campaign_name}`,
    ...row.windows.map((w) => windowSentence(w, leads)),
    leads
      ? `Our target is about ${usd(row.target_cpa_usd)} per lead.`
      : `Our target is about ${usd(row.target_cpa_usd)} per conversion.`,
    'Do you want to keep running this campaign as it is, or should we change or pause it?',
  ];
  return lines.join('\n');
}

export function raisedLine(by: string, at: string | null): string {
  const when = at ? new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return when ? `Raised with client by ${by} on ${when}.` : `Raised with client by ${by}.`;
}

export function snoozedLine(row: CampaignReviewRow): string {
  const s = row.snooze;
  const reason = s?.reason ? SNOOZE_REASON_LABEL[s.reason] : 'Snoozed';
  const until = s?.until
    ? new Date(s.until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  return until
    ? `${row.campaign_name}: ${reason}, until ${until} or until its spend doubles.`
    : `${row.campaign_name}: ${reason}.`;
}

export const MARKS_UNAVAILABLE =
  'Raise with client and Snooze are not set up here yet. Ask the team to finish setting them up.';

/** A failed Raise or Snooze, in plain words. Never the server's own text. */
export function markErrorText(err: unknown): string {
  const e = (err ?? {}) as {
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const status = e.response?.status;
  const detail = e.response?.data?.detail as { error?: string } | undefined;
  if (!e.response) return 'Core could not be reached. Check your connection, then try again.';
  if (status === 503 && detail?.error === 'campaign_review_marks_unavailable') {
    return MARKS_UNAVAILABLE;
  }
  if (status === 400 && detail?.error === 'campaign_not_in_account') {
    return "That campaign is not in this workspace's Google Ads account, so nothing was saved. Refresh the page.";
  }
  if (status === 502 && detail?.error === 'campaign_spend_unavailable') {
    return "Core could not read this campaign's spend from Google Ads, so it was not snoozed. Try again in a minute.";
  }
  if (status === 403) {
    return 'Your role on this workspace cannot do this. Ask a workspace admin for editor access.';
  }
  if (status === 401) return 'Your session ended. Sign in again, then try again.';
  return 'Could not save that. Try again in a minute.';
}
